/**
 * First-run seed: the 24-cell City View price schedule and the shared defaults.
 * Coaches are deliberately not seeded — they are assigned in Settings.
 *
 * Idempotent by design. It runs on every app boot and only fills gaps, so a
 * future release that adds a price row seeds just that one without resetting
 * prices the owner has already changed.
 */
import {
  db,
  getSetting,
  newRowFields,
  SETTING_KEYS,
  setSetting,
  SHARED_SETTING_KEYS,
} from './db';
import {
  DEFAULT_WALK_IN_PRICE_PIASTRES,
  defaultPriceSchedule,
} from '@shared/pricing';
import { DEFAULT_COACH_RATES, DEFAULT_SPLIT, serialiseCoachRates } from '@shared/payouts';
import { CURRENCY_CODE } from '@shared/money';
import { nowTimestamp } from '@shared/dates';
import { reseedSkIdCounter } from './ids';

export interface SeedResult {
  pricesAdded: number;
  coachesAdded: number;
  firstRun: boolean;
}

export async function seedDatabase(): Promise<SeedResult> {
  const firstRun = (await getSetting(SETTING_KEYS.seededAt)) === null;

  const result = await db.transaction(
    'rw',
    [db.price_schedule, db.app_settings, db.settings],
    async () => {
      /* ---- price schedule ---- */
      const existingPrices = new Set((await db.price_schedule.toArray()).map((p) => p.price_key));
      const newPrices = defaultPriceSchedule()
        .filter((e) => !existingPrices.has(e.key))
        .map((e) => ({
          ...newRowFields(),
          price_key: e.key,
          price_piastres: e.pricePiastres,
        }));
      if (newPrices.length) await db.price_schedule.bulkAdd(newPrices);

      // Coaches are NOT seeded — they are assigned in Settings. A class with
      // nobody assigned still earns its 40%, shown as "Unassigned" in the
      // payout report, so the money is visible rather than silently lost.

      /* ---- shared settings (synced) — never clobber an owner's change ---- */
      const shared: [string, string][] = [
        [SHARED_SETTING_KEYS.gymName, 'SK Martial Arts Academy'],
        [SHARED_SETTING_KEYS.walkInPrice, String(DEFAULT_WALK_IN_PRICE_PIASTRES)],
        [SHARED_SETTING_KEYS.coachPercent, String(DEFAULT_SPLIT.coachPercent)],
        [SHARED_SETTING_KEYS.managerPercent, String(DEFAULT_SPLIT.managerPercent)],
        [SHARED_SETTING_KEYS.cityViewPercent, String(DEFAULT_SPLIT.cityViewPercent)],
        // Coaches on their own rate. Seeded once and then owned by Settings —
        // the loop below never overwrites a key that already exists, so a rate
        // the manager has changed survives every future release.
        [SHARED_SETTING_KEYS.coachRates, serialiseCoachRates(DEFAULT_COACH_RATES)],
      ];

      for (const [key, value] of shared) {
        const existing = await db.app_settings.where('key').equals(key).first();
        if (!existing) await db.app_settings.add({ ...newRowFields(), key, value });
      }

      /* ---- device-local defaults ---- */
      if ((await db.settings.get(SETTING_KEYS.currency)) === undefined) {
        await db.settings.put({
          key: SETTING_KEYS.currency,
          value: CURRENCY_CODE,
          updated_at: nowTimestamp(),
        });
      }

      return { pricesAdded: newPrices.length, coachesAdded: 0, firstRun };
    }
  );

  await reseedSkIdCounter();
  if (firstRun) await setSetting(SETTING_KEYS.seededAt, nowTimestamp());

  return result;
}
